import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AbstractOnboardingPageComponent } from 'src/app/core/abstracts/components/onboarding-page.abstract';
import { CLientTier } from 'src/app/core/enums/client-tier.enum';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { OnboardingModuleStatuses } from 'src/app/core/enums/onboarding-module-status.enum';
import { IOptions } from 'src/app/core/interfaces/common/common/options.interface';
import { IPageContent } from 'src/app/core/interfaces/common/content/page-content.interface';
import { IFinancialProfile } from 'src/app/core/interfaces/onboarding/financial-profile/financial-profile.interface';
import { IStatefulOptions } from 'src/app/core/interfaces/onboarding/page-state/stateful-options.interface';
import { ITransition } from 'src/app/core/interfaces/onboarding/page-state/transition.interface';
import { PageContent } from 'src/app/core/models/common/page-content.model';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { EnrollmentExtendedService } from 'src/app/core/services/onboarding/enrollment-extended.service';
import { FinancialProfileService } from 'src/app/core/services/onboarding/finacial-profile.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import * as FinancialProfileActions from 'src/app/core/store/onboarding/actions/financial-profile.actions';
import { selectAllEnrollment } from 'src/app/core/store/onboarding/reducers+selectors/enrollment.selector';
import { selectInvoiceTerm } from 'src/app/core/store/onboarding/reducers+selectors/financial-profile.selector';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { StatefulAnswerButtonListComponent } from 'src/app/shared/onboarding/stateful-answer-button-list/stateful-answer-button-list.component';
import { ContentIDConstants } from '../content-id.constants';

@Component({
  selector: 'app-invoice-term',
  templateUrl: './invoice-term.component.html',
  styleUrls: ['./invoice-term.component.scss'],
})
export class InvoiceTermComponent
  extends AbstractOnboardingPageComponent
  implements OnInit, OnDestroy
{
  @ViewChild('buttonList', { static: false })
  buttonList: StatefulAnswerButtonListComponent;
  belowThresholdState: PageState;
  aboveThresholdState: PageState;
  homeState: PageState;
  backState: PageState;
  skipState: PageState;
  questionText = 'What is your typical invoice term?';
  tooltipContent: IPageContent;
  options: IStatefulOptions[];
  payload: IFinancialProfile;
  subscriptions: Subscription[] = [];
  pageStateId: string;

  fpStatus: OnboardingModuleStatuses;
  storedDashboardStateId: string;

  constructor(
    private route: ActivatedRoute,
    private ps: PageStateService,
    private fp: FinancialProfileService,
    private ees: EnrollmentExtendedService,
    private store: Store<IAppState>,
    public router: Router
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.data.subscribe((data) => {
        const { transitions } = data['data'];
        const { contentDict } = data['data'];
        this.populatePageStates(transitions);
        this.populatePageContent(contentDict);
        this.populateOptionStates(data['options']);
        this.fpStatus = OnboardingModuleStatuses.ACTIVE;
        const enrollmentExtended$ = this.store.select(selectAllEnrollment);
        this.subscriptions.push(
          enrollmentExtended$.subscribe((props) => {
            this.fpStatus = props.financialProfileStatusId;
            this.storedDashboardStateId = props.dashboardStateId;
          })
        );

        this.subscriptions.push(
          this.route.queryParams.subscribe(async (params) => {
            this.pageStateId = params['stateId'];
            if (this.fpStatus === OnboardingModuleStatuses.ACTIVE) {
              console.log('fp active');
              this.ees.updateFinancialProfileStateId(
                this.pageStateId,
                this.homeState.id
              );
            } else if (this.fpStatus === OnboardingModuleStatuses.COMPLETED) {
              console.log('fp completed');
              const storedDashboard = await this.ps.getPageStateById(
                this.storedDashboardStateId
              );
              this.ees.updateOnlyFinancialProfileStateId(this.pageStateId);
              this.aboveThresholdState = storedDashboard;
            }
          })
        );
      })
    );
    this.payload = this.fp.getPayload();
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  populatePageStates(transitions: ITransition[]) {
    this.belowThresholdState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.TERM_BELOW_THRESHOLD,
      transitions
    );
    this.aboveThresholdState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.TERM_ABOVE_THRESHOLD,
      transitions
    );
    this.homeState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.HOME,
      transitions
    );
    this.backState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.BACK,
      transitions
    );
    this.skipState = this.ps.getPageStateByEventAndTransitions(
      EventTypes.SKIP,
      transitions
    );
  }

  populatePageContent(contentDict: Map<number, IPageContent>) {
    this.questionText =
      contentDict.get(ContentIDConstants.question)?.displayText ??
      this.questionText;
    this.tooltipContent =
      contentDict.get(ContentIDConstants.tooltip) ?? new PageContent();
  }

  populateOptionStates(options: IOptions[]) {
    let selectedId = -10;
    const props$ = this.store.select(selectInvoiceTerm);
    this.subscriptions.push(
      props$.subscribe((props) => {
        selectedId = props.invoiceTerm.id as number;
      })
    );
    // We will need to do threshold filtering
    this.options = options.map((option) => ({
      ...option,
      state: this.belowThresholdState,
      selected: option.id === selectedId,
    }));
  }

  async handleOutput(outputValue: number) {
    const clickedOption: IStatefulOptions | undefined = this.options.find(
      (item, i) => i === outputValue
    );
    if (clickedOption) {
      this.payload.invoiceTermId = clickedOption.id;
      this.payload.invoiceTermName = clickedOption.value;
      this.payload.pageStateId = this.pageStateId;
      this.payload.needToSaveInvoicePerMonth = false;
      this.payload.needToSaveInvoiceTerm = true;
      this.payload.needToUploadBankStatement = false;
    } else {
      this.payload.invoiceTermId = -1;
      this.payload.invoiceTermName = '';
      this.payload.pageStateId = '-1';
    }
    await this.submit();
  }

  async submit() {
    this.store.dispatch(
      FinancialProfileActions.InvoiceTermAction({
        invoiceTerm: {
          id: this.payload.invoiceTermId,
          value: this.payload.invoiceTermName,
        },
      })
    );
    const data = await this.fp.saveFinancialProfile(this.payload);
    if (!data.isSuccess) return false;

    // redirect based on the client tier
    if (data.clientTier === (CLientTier.BasicTier as number)) {
      if (this.fpStatus === OnboardingModuleStatuses.ACTIVE) {
        await Promise.allSettled([
          this.ees.completeFiancialProfile(),
          this.ees.activateCustomerPortfolio(),
          this.ees.updateDashboardStateId(this.aboveThresholdState.id),
        ]);
      }
      await this.buttonList.navigate(this.aboveThresholdState);
      return true;
    }
    if (this.fpStatus === OnboardingModuleStatuses.ACTIVE) {
      await Promise.allSettled([
        this.ees.updateDashboardStateId(this.belowThresholdState.id),
      ]);
    }
    await this.buttonList.navigate(this.belowThresholdState);
    return true;
  }
}
